import React, { useState } from "react";

import ContentContainer from "../../components/ContentContainer/ContentContainer";
import Spacer from "../../components/Spacer/Spacer";
import Loading from "../../components/Loading/Loading";
import InfoPopover, { InfoPopoverSection } from "../../components/InfoPopover/InfoPopover";
import ExpandableTable from "../../components/ExpandableTable/ExpandableTable";

import AccuracyLeaderboard from "./AccuracyLeaderboard";
import AccuracyGameweekChart from "./AccuracyGameweekChart";
import PredictorDetailPanel from "./PredictorDetailPanel";

import { useDataFetch } from "../../useDataFetch";

import "../PriceEvaluation/PriceEvaluation.css";
import "../PriceEvaluation/PriceEvaluationLeaderboard.css";
import "./PredictionsAccuracy.css";

const PredictionsAccuracy = () => {
    const { data, loading, error } = useDataFetch('/fetch-points-evaluation-data');
    const [activeSection, setActiveSection] = useState("regular");
    const [chartMetric, setChartMetric] = useState("combined");
    const [selectedPredictor, setSelectedPredictor] = useState(null);
    const [selectedSection, setSelectedSection] = useState("regular");
    const [timeframe, setTimeframe] = useState("season");
    const [selectedGw, setSelectedGw] = useState(null);

    const handleSelectPredictor = (predictor, sectionKey) => {
        setSelectedPredictor(predictor);
        setSelectedSection(sectionKey || "regular");
    };

    const sections = ["regular", "starting"];
    const sectionNames = {
        regular: "Normal",
        starting: "Assume Starting",
    };

    const explainerText = () => (
        <div className="page-part-white" style={{ paddingBottom: 0 }}>
            <div className="page-info-row accuracy-info-row">
                <InfoPopover storageKey="leaderboards" title="Predictor accuracy">
                    <p>
                        There are many (many) websites that predict <b>FPL player points</b>, but little
                        to no information out there that will tell you which are actually any good. I was
                        annoyed by that, so I decided to start tracking them myself.
                    </p>

                    <InfoPopoverSection title="The Leaderboard">
                        <p>
                            This leaderboard ranks all <b>free</b> FPL points predictors by how accurate they
                            are. The predictors are split into two categories:
                        </p>
                        <ul>
                            <li>
                                <b>Normal</b> — websites that predict the points of a player for the upcoming
                                gameweek, no strings attached. This is also the largest group.
                            </li>
                            <li>
                                <b>Assume starting</b> — predictions given that a player is starting, more
                                similar to how bookies determine their prices. Not directly comparable to the
                                other category.
                            </li>
                        </ul>
                    </InfoPopoverSection>

                    <InfoPopoverSection title="How are they evaluated?">
                        <p>
                            I collect each site's predictions as close to the deadline as I can. After the
                            gameweek is finished, their predicted points are compared to the actual points the
                            players got, scored on 3 metrics:
                        </p>
                        <ul>
                            <li>
                                <b>Error</b> — how far from the truth each prediction is, measured as the RMSE
                                for the nerds among us.
                            </li>
                            <li>
                                <b>Order</b> — did the model order the players in the same order as the actual
                                outcome? Having high scorers above low scorers matters, as this is how players
                                are selected by users. Measured by C-index.
                            </li>
                            <li>
                                <b>Returns</b> — the Error, but only for players who returned (5 points or
                                more). Predicting well for high scoring players is more important than
                                predicting whether a shit player will get 1 or 2 points.
                            </li>
                        </ul>
                        <p>
                            The <b>total score</b> is the average of those 3 metrics, displayed as the
                            percentage improvement over the benchmark.
                        </p>
                    </InfoPopoverSection>

                    <InfoPopoverSection title="What is the benchmark?">
                        <p>
                            The benchmark is what we compare the models to. All metrics are then shown as
                            how much better or worse it did than the benchmark.
                            <br /><br />
                            For <b>Normal</b> it's the average points that player had per game so far this
                            season; for <b>Assume starting</b>, the average points when starting. Fallback is
                            the average points from last season (e.g. for GW1).
                        </p>
                    </InfoPopoverSection>
                </InfoPopover>
            </div>
        </div>
    );

    const timeframeNames = {
        season: "Season",
        last5gws: "Last 5 GWs",
        gwbygw: "GW by GW",
    };

    // "Last 5 GWs" is only meaningful once the season is long enough to have a
    // trailing window distinct from the season total — require at least one
    // predictor with 6+ gameweeks of actual (non-null) data.
    const maxPredictorGameweeks = (sectionsData) => {
        let max = 0;
        ["regular", "starting"].forEach((section) => {
            const byGameweek = sectionsData?.[section]?.by_gameweek || {};
            const counts = {};
            Object.values(byGameweek).forEach((entries) => {
                entries.forEach((e) => {
                    if (e.predictor !== "benchmark" && e.combined_score !== null && e.combined_score !== undefined) {
                        counts[e.predictor] = (counts[e.predictor] || 0) + 1;
                    }
                });
            });
            Object.values(counts).forEach((c) => { if (c > max) max = c; });
        });
        return max;
    };

    // Union of every gameweek number that appears for either section, ascending.
    const collectAvailableGws = (sectionsData) => {
        const set = new Set();
        ["regular", "starting"].forEach((section) => {
            Object.keys(sectionsData?.[section]?.by_gameweek || {}).forEach((k) => set.add(Number(k)));
        });
        return Array.from(set).sort((a, b) => a - b);
    };

    const showLast5Gws = !loading && data ? maxPredictorGameweeks(data) >= 6 : false;
    const availableGws = !loading && data ? collectAvailableGws(data) : [];
    const timeframes = [
        "season",
        ...(showLast5Gws ? ["last5gws"] : []),
        ...(availableGws.length > 0 ? ["gwbygw"] : []),
    ];
    const effectiveTimeframe = timeframes.includes(timeframe) ? timeframe : "season";
    const effectiveSelectedGw = selectedGw !== null && availableGws.includes(selectedGw)
        ? selectedGw
        : availableGws[availableGws.length - 1];

    // Reshape one gameweek's raw by_gameweek entries into the {combined, overall, returns}
    // shape AccuracyLeaderboard expects (the season/last5gws sub-objects use the same shape).
    const gwLeaderboardData = (sectionData, gw) => {
        const entries = sectionData?.by_gameweek?.[String(gw)] || [];
        return {
            combined: entries.map((e) => ({ predictor: e.predictor, combined_score: e.combined_score })),
            overall: entries.map((e) => ({ predictor: e.predictor, rmse: e.rmse, c_index: e.c_index })),
            returns: entries.map((e) => ({ predictor: e.predictor, rmse: e.returns_rmse })),
        };
    };

    const timeframeTabs = () => {
        if (timeframes.length < 2) return null;
        return (
            <div className="leaderboard-half noSelect" style={{ width: "100%" }}>
                <div className="leaderboard-tabs">
                    {timeframes.map((tf) => (
                        <button
                            key={tf}
                            className={tf === effectiveTimeframe ? "leaderboard-tab active" : "leaderboard-tab inactive"}
                            onClick={() => setTimeframe(tf)}
                        >
                            {timeframeNames[tf]}
                        </button>
                    ))}
                </div>
                {effectiveTimeframe === "gwbygw" && (
                    <div className="accuracy-gw-select-row noSelect">
                        <label htmlFor="accuracy-gw-select">Gameweek</label>
                        <select
                            id="accuracy-gw-select"
                            value={effectiveSelectedGw}
                            onChange={(e) => setSelectedGw(Number(e.target.value))}
                        >
                            {availableGws.map((gw) => (
                                <option key={gw} value={gw}>GW{gw}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    };

    const sectionTabs = () => (
        <div className="accuracy-section-tabs">
            {sections.map((s) => (
                <button
                    key={s}
                    className={`accuracy-section-tab ${s === activeSection ? "active" : "inactive"}`}
                    onClick={() => setActiveSection(s)}
                >
                    {sectionNames[s]}
                </button>
            ))}
        </div>
    );

    const combinedLeaderboards = () => {
        const panelSectionData = data[selectedSection];
        const isGwByGw = effectiveTimeframe === "gwbygw";
        const regularSource = isGwByGw
            ? gwLeaderboardData(data.regular, effectiveSelectedGw)
            : data.regular[effectiveTimeframe];
        const startingSource = isGwByGw
            ? gwLeaderboardData(data.starting, effectiveSelectedGw)
            : data.starting[effectiveTimeframe];
        return (
            <div className="page-part-white">
                <ContentContainer containerStyle="content-container-fixed-width">
                    <div className="accuracy-detail-layout">
                        <ExpandableTable>
                            <div className="accuracy-container noSelect">
                                {timeframeTabs()}
                                <div className="accuracy-sub-container noSelect">
                                    <AccuracyLeaderboard
                                        data={regularSource.combined}
                                        overall={regularSource.overall}
                                        returns={regularSource.returns}
                                        title="Normal"
                                        sectionKey="regular"
                                        onSelectPredictor={handleSelectPredictor}
                                        selectedPredictor={selectedSection === "regular" ? selectedPredictor : null}
                                        pendingLabel={isGwByGw ? "No data collected" : "Pending more data"}
                                        byGameweek={effectiveTimeframe === "season" ? data.regular.by_gameweek : undefined}
                                    />
                                    <hr className="leaderboard-divider" />
                                    <AccuracyLeaderboard
                                        data={startingSource.combined}
                                        overall={startingSource.overall}
                                        returns={startingSource.returns}
                                        title="Assume starting"
                                        sectionKey="starting"
                                        onSelectPredictor={handleSelectPredictor}
                                        selectedPredictor={selectedSection === "starting" ? selectedPredictor : null}
                                        pendingLabel={isGwByGw ? "No data collected" : "Pending more data"}
                                        byGameweek={effectiveTimeframe === "season" ? data.starting.by_gameweek : undefined}
                                    />
                                </div>
                            </div>
                        </ExpandableTable>
                        <PredictorDetailPanel
                            predictor={selectedPredictor}
                            onClose={() => setSelectedPredictor(null)}
                            byGameweek={panelSectionData.by_gameweek}
                            byPredictor={panelSectionData.by_predictor}
                            timeframe={effectiveTimeframe}
                            selectedGw={effectiveSelectedGw}
                        />
                    </div>
                </ContentContainer>
                <div className="explainer-text">
                    <div style={{ maxWidth: "450px", margin: "0 auto", textAlign: "center" }}>
                        <p>
                            Values show combined % improvement over the benchmark.
                            Click any predictor to see detailed stats.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const gameweekChart = () => {
        const sectionData = data[activeSection];
        return (
            <div className="page-part-grey">
                {sectionTabs()}
                <div style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
                    <p className="subtitle-text" style={{ marginTop: 0 }}>
                        Performance by gameweek
                    </p>
                </div>
                <div className="accuracy-chart-metric-tabs">
                    {[
                        { key: "combined", label: "Combined" },
                        { key: "rmse", label: "RMSE" },
                        { key: "cindex", label: "C-Index" },
                        { key: "returns_rmse", label: "Returns RMSE" },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            className={`accuracy-chart-metric-tab ${chartMetric === tab.key ? "active" : "inactive"}`}
                            onClick={() => setChartMetric(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <ContentContainer containerStyle="content-container-fixed-width">
                    <AccuracyGameweekChart
                        data={sectionData.by_gameweek}
                        metric={chartMetric}
                    />
                </ContentContainer>
            </div>
        );
    };

    const sectionData = !loading && data ? data[activeSection] : null;

    return (
        <div>
            {explainerText()}
            {loading && <Loading />}
            {!loading && data && (
                <div>
                    {combinedLeaderboards()}
                    {/* Gameweek trend chart temporarily disabled — deemed redundant with the
                        per-predictor gameweek chart in the detail panel. Code kept in case
                        we want it back.
                    <Spacer style={{ padding: "15px" }} />
                    {gameweekChart()}
                    */}
                </div>
            )}
        </div>
    );
};

export default PredictionsAccuracy;

